'use client';

import React from 'react';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { TRANSLATED_ROUTES } from '@/lib/i18n/routes';
import { Languages } from 'lucide-react';

interface LanguageSwitcherProps {
  variant?: 'desktop' | 'mobile';
  className?: string;
}

export function LanguageSwitcher({ variant = 'desktop', className = '' }: LanguageSwitcherProps) {
  const pathname = usePathname() || '/';
  const tSwitcher = useTranslations('switcher');

  // Determine active locale and normalized path
  const isHindi = pathname === '/hi' || pathname.startsWith('/hi/');

  // Compute English counterpart
  let enHref = '/';
  if (isHindi) {
    const stripped = pathname.slice(3); // remove '/hi'
    enHref = stripped === '' ? '/' : stripped;
  } else {
    enHref = pathname;
  }

  // Compute Hindi counterpart
  let hiHref = '/hi';
  if (isHindi) {
    hiHref = pathname;
  } else {
    let normalized = pathname;
    if (normalized.length > 1 && normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
    }
    const routeKey = normalized === '/' ? '' : normalized;
    if ((TRANSLATED_ROUTES as readonly string[]).includes(routeKey)) {
      hiHref = routeKey === '' ? '/hi' : `/hi${routeKey}`;
    } else {
      hiHref = '/hi';
    }
  }

  if (variant === 'mobile') {
    return (
      <div className={`flex items-center gap-2 p-2 rounded-xl bg-[rgba(5,10,36,0.96)] border border-[rgba(255,255,255,0.14)] backdrop-blur-md shadow-lg ${className}`}>
        <Languages className="w-4 h-4 text-[#5ab4ff] ml-2" />
        <span className="text-xs font-mono text-[rgba(220,236,255,0.82)] mr-auto">{tSwitcher('label')}</span>
        <div className="flex items-center gap-1 bg-white/5 p-0.5 rounded-lg border border-white/10">
          <NextLink
            href={enHref}
            className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${
              !isHindi
                ? 'bg-white/20 text-white font-bold shadow-xs border border-white/30'
                : 'text-[rgba(220,236,255,0.78)] hover:text-white'
            }`}
          >
            {tSwitcher('en')}
          </NextLink>
          <NextLink
            href={hiHref}
            className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${
              isHindi
                ? 'bg-white/20 text-white font-bold shadow-xs border border-white/30'
                : 'text-[rgba(220,236,255,0.78)] hover:text-white'
            }`}
          >
            {tSwitcher('hi')}
          </NextLink>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative inline-flex items-center rounded-full bg-[rgba(5,10,36,0.96)] border border-[rgba(255,255,255,0.14)] p-0.5 text-xs font-medium shadow-md h-[30px] backdrop-blur-md ${className}`}>
      <NextLink
        href={enHref}
        className={`px-2.5 py-1 rounded-full transition-all text-xs font-semibold ${
          !isHindi
            ? 'bg-white/20 text-white font-bold shadow-xs border border-white/30'
            : 'text-[rgba(220,236,255,0.78)] hover:text-white'
        }`}
      >
        {tSwitcher('en')}
      </NextLink>
      <NextLink
        href={hiHref}
        className={`px-2.5 py-1 rounded-full transition-all text-xs font-semibold ${
          isHindi
            ? 'bg-white/20 text-white font-bold shadow-xs border border-white/30'
            : 'text-[rgba(220,236,255,0.78)] hover:text-white'
        }`}
      >
        {tSwitcher('hi')}
      </NextLink>
    </div>
  );
}
